import React, { Suspense, lazy, useMemo, useEffect } from "react";
import { RouterProvider, createBrowserRouter, Navigate } from "react-router-dom";
import { SnackbarProvider } from "notistack";
import { useSelector, useDispatch } from "react-redux";
import { Box, CircularProgress, ThemeProvider, useMediaQuery } from "@mui/material";
import CssBaseline from '@mui/material/CssBaseline';
import { getTheme } from "./theme";
import { setFeatureFlags } from "./store/featureSlice";
import { useLanguageSync } from "./hooks/useLanguageSync";
import api from "./api";


import Layout from "./pages";
import Logout from "./components/Logout";
import ErrorPage from "./components/ErrorPage";

const Dashboard = lazy(() => import("./pages/Dashboard"));
const Login = lazy(() => import("./pages/Login"));
const Profile = lazy(() => import("./pages/Profile"));
const ProfileEdit = lazy(() => import("./pages/ProfileEdit"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ConfirmEmail = lazy(() => import("./pages/ConfirmEmail"));
const SettingsPage = lazy(() => import("./pages/Settings"));
const EditReport = lazy(() => import("./pages/EditReport"));
const ResetPasswordConfirm = lazy(() => import("./pages/ResetPasswordConfirm"));
const ReportCompletionSuccess = lazy(() => import('./pages/ReportCompletionSuccess'));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const Help = lazy(() => import("./pages/Help"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Maintenance = lazy(() => import("./pages/Maintenance"));

function PageFallback() {
  return (
    <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center">
      <CircularProgress />
    </Box>
  );
}

function lazyPage(element) {
  return <Suspense fallback={<PageFallback />}>{element}</Suspense>;
}

function AdminRoute({ children }) {
  const isAuth = useSelector((state) => state.user.isAuth);
  if (!isAuth) {
    return <Navigate to="/auth/login" replace />;
  }
  return children;
}

const router = createBrowserRouter([
    // Public Maintenance route
    {
      path: "maintenance",
      element: lazyPage(<Maintenance />),
    },

    // Public (auth) routes
    {
      path: "auth",
      children: [
        { index: true, element: lazyPage(<Login />) },
        { path: "login", element: lazyPage(<Login />) },
        { path: "change-password", element: lazyPage(<ChangePassword />) },
        { path: "reset-password", element: lazyPage(<ResetPassword />) },
        { path: "reset-password/confirm/:uid/:token", element: lazyPage(<ResetPasswordConfirm />) },
        { path: "confirm-email", element: lazyPage(<ConfirmEmail />) },
      ],
    },

    // Protected routes
  {
    path: "/",
      element: (
        <AdminRoute>
          <Layout />
        </AdminRoute>
      ),
      errorElement: <ErrorPage />,
    children: [
      { index: true, element: lazyPage(<Dashboard />) },
        {
          path: "reports/:reportId/edit",
          element: lazyPage(<EditReport />),
        },
        {
          // TODO: is it secure? Should we protect this route further against mis-use?
          path: "reports/completed",
          element: lazyPage(<ReportCompletionSuccess />),
        },
        {
          path: "profile",
          children: [
            { index: true, element: lazyPage(<Profile />) },
            { path: "edit", element: lazyPage(<ProfileEdit />) },
          ],
        },
        { path: "logout", element: <Logout /> },
      { path: "settings", element: lazyPage(<SettingsPage />) },
      { path: "notifications", element: lazyPage(<Notifications />) },
      { path: "help", element: lazyPage(<Help />) },
    ],
  },

]);

export default function App() {
  const dispatch = useDispatch();
  const themeMode = useSelector((state) => state.theme.mode);
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const themeSwitcherEnabled = useSelector((state) => state.features.themeSwitcherEnabled);

  useLanguageSync();

  useEffect(() => {
    const loadFeatureFlags = async () => {
      try {
        const response = await api.get('/settings/feature-flags/');
        dispatch(setFeatureFlags(response.data));
      } catch (error) {
        console.error('Errore nel caricamento delle feature flags:', error);
        dispatch(setFeatureFlags([]));
      }
    };

    loadFeatureFlags();
  }, [dispatch]);

  const activeMode = useMemo(() => {
    if (!themeSwitcherEnabled) {
      return 'light';
    }
    
    if (themeMode === 'system') {
      return prefersDarkMode ? 'dark' : 'light';
    }
    return themeMode;
  }, [themeMode, prefersDarkMode, themeSwitcherEnabled]);

  const theme = useMemo(() => getTheme(activeMode), [activeMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <RouterProvider router={router} />
      </SnackbarProvider>
    </ThemeProvider>
  );
}
